import styled from "@emotion/styled";
import type { DialogProps, SlideProps } from "@mui/material";
import { Dialog, DialogActions, DialogContent, DialogTitle, Slide } from "@mui/material";
import type { CSSProperties, ReactNode, Ref } from "react";
import { forwardRef } from "react";

import { useFormatMessage } from "@skilitics/localization";

import { Button } from "../buttons/Buttons";
import { transientOptions } from "../utilities";
import { ConfirmationDialogButtons } from "./ConfirmationDialogButtons";

const DialogContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

export const StyledDialogContent = styled(DialogContent, transientOptions)<{ $nopadding: boolean }>`
  padding: ${({ $nopadding }) => ($nopadding ? "0" : "15px")};
`;

export type ModalDialogProps = Omit<DialogProps, "style" | "open"> & {
  header?: ReactNode;
  hideDividers?: boolean;
  open?: boolean;
  children?: ReactNode;
  noPadding?: boolean;
  actionsStyle?: CSSProperties;
} & (
    | {
        buttons?: ReactNode | "showClose";
        cancelLabel?: ReactNode;
        confirmLabel?: ReactNode;
        onConfirm?: (confirmed: boolean) => void;
      }
    | {
        buttons: "confirm";
        cancelLabel: ReactNode;
        confirmLabel: ReactNode;
        onConfirm: (confirmed: boolean) => void;
      }
  );

const Transition = forwardRef<Ref<unknown>, SlideProps>((props, ref) => {
  return <Slide direction="up" ref={ref} {...props} />;
});
Transition.displayName = "Transition";

export const ModalDialog = ({
  buttons,
  cancelLabel,
  children,
  confirmLabel,
  header,
  hideDividers,
  noPadding = false,
  onClose,
  onConfirm,
  open = false,
  actionsStyle,
  ...rest
}: ModalDialogProps) => {
  const formatMessage = useFormatMessage(formatStrings);
  return (
    <Dialog
      slots={{ transition: Transition }}
      onClose={onClose}
      open={open}
      {...rest}
      data-test="modal-dialog-container"
    >
      <DialogContentContainer data-test="content-container">
        {header ? (
          <>
            <DialogTitle data-test="header-container">{header}</DialogTitle>
            <StyledDialogContent
              dividers={hideDividers ? undefined : true}
              $nopadding={noPadding}
              data-test="body-container"
            >
              {children}
            </StyledDialogContent>
          </>
        ) : (
          children
        )}
      </DialogContentContainer>
      {buttons && (
        <DialogActions style={actionsStyle} data-test="actions-container">
          {buttons === "showClose" ? (
            <Button autoFocus onClick={() => onClose?.({}, "backdropClick")} data-test="close-button">
              {formatMessage("close")}
            </Button>
          ) : buttons === "confirm" && onConfirm ? (
            <ConfirmationDialogButtons cancelLabel={cancelLabel} confirmLabel={confirmLabel} onConfirm={onConfirm} />
          ) : (
            buttons
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

const formatStrings = {
  close: {
    en: "Close",
  },
};
